import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import request, { getImageBaseUrl } from 'helpers/request';
import { getConfigurations, getLoadedConfiguration } from 'src/redux/garage/selectors';
import {
  bikeModelSelector,
  bikeColourSelector,
  getPack,
  getBikeAccessories,
  getPackAccessories,
  getSelectedWarrantyPack,
  getSelectedServicePack,
} from 'src/redux/bike/selectors';
import { getAccessoryImage } from 'helpers/accessories';
import { setConfigurations, setLoadedConfiguration, setSaving } from 'src/redux/garage/actions';
import { resetTagging } from 'src/redux/tagging/actions';

const useSaveToGarage = () => {
  const configurations = useSelector(getConfigurations);
  const loadedConfiguration = useSelector(getLoadedConfiguration);
  const isConfigurationSaved =
    loadedConfiguration && configurations.some(configuration => configuration.id === loadedConfiguration.id);
  const bikeModel = useSelector(bikeModelSelector);
  const bikeColour = useSelector(bikeColourSelector);
  const accessories = useSelector(getBikeAccessories);
  const packAccessories = useSelector(getPackAccessories);
  const selectedServicePack = useSelector(getSelectedServicePack);
  const selectedWarrantyPack = useSelector(getSelectedWarrantyPack);
  const pack = useSelector(getPack);
  const dispatch = useDispatch();

  const saveToGarage = async (emailAddress, dataConsentDoNotContact, recieveCommunication, callback = () => {}) => {
    dispatch(setSaving(true));

    if (emailAddress) {
      localStorage.setItem('garageEmailAddress', emailAddress);
    }

    const imagePromises = [
      new Promise(resolve => {
        const bikeImage = new Image();
        bikeImage.crossOrigin = 'anonymous';
        bikeImage.src = getImageBaseUrl() + bikeColour.rightImage;

        bikeImage.onload = () => resolve(bikeImage);
      }),
      ...[...accessories, ...packAccessories]
        .map(accessory => getAccessoryImage(accessories, accessory))
        .filter(accImage => accImage && accImage.rightImage)
        .sort((a, b) => a.layer - b.layer)
        .map(
          accImage =>
            new Promise(resolve => {
              const accessoryImage = new Image();
              accessoryImage.crossOrigin = 'anonymous';
              accessoryImage.src = getImageBaseUrl() + accImage.rightImage.src;

              accessoryImage.onload = () => resolve(accessoryImage);
            })
        ),
    ];

    const images = await Promise.all(imagePromises);

    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.visibility = 'hidden';
    canvas.width = 400;
    canvas.height = 225;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');

    images.forEach(image => {
      ctx.drawImage(image, 0, 0, 400, 225);
    });

    const imageData = canvas.toDataURL();

    document.body.removeChild(canvas);

    const body = {
      User: emailAddress || localStorage.getItem('garageEmailAddress'),
      Model: bikeModel.id,
      Colour: bikeColour.id,
      Accessories: [...accessories, ...(!pack || pack.isLocked ? [] : packAccessories)].map(accessory => accessory.id),
      Packs: pack ? [pack.id] : [],
      Extras: [selectedServicePack?.id, selectedWarrantyPack?.id].filter(extra => !!extra),
      Image: imageData.split(',')[1],
      DataConsentDoNotContact: !!dataConsentDoNotContact,
      MarketingOptIn: !!recieveCommunication,
    };

    const url = isConfigurationSaved
      ? `/api/configurator/garage/${loadedConfiguration.id}`
      : '/api/configurator/garage';

    const res = await request(url, {
      method: isConfigurationSaved ? 'PUT' : 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
    });

    const userEmail = window.localStorage.getItem('garageEmailAddress');
    if (userEmail) {
      const { data } = await request(`/api/configurator/garage?user=${userEmail}`);

      dispatch(setConfigurations(data ? data.configurations : []));
      dispatch(
        setLoadedConfiguration(
          data ? data.configurations.find(configuration => configuration.id === res.data.Id) : null
        )
      );
      dispatch(resetTagging());
    }

    dispatch(setSaving(false));
    callback(res.data.Id);
  };

  return saveToGarage;
};

export default useSaveToGarage;
